<template>
  <wrap />
</template>

<script setup>
import Wrap from '@/views/index.vue';
import { onMounted, watch } from 'vue';
import store from '@/store';
import router from '@/router';
import { trackSessionOpen } from '@/common/utils/retentionTracker';
import ServiceExec from '@/common/utils/ServiceExec';
import { referrals } from '@/common/service/apiList';
import { isApp } from '@/common/utils/module/IsMobileSupport';
import { isPlaceNo } from '@/common/utils/qrPick'; // 260917 pending-claim placeNo 정수 판정(qr.carry.place 저장 게이트)
import { markQrInstallReferrerOnce } from '@/common/utils/qrInstallCarry'; // 260928 현판 → 손님 가입 경로(안드 Play referrer)
// 이름은 IOS지만 실제로는 플랫폼 공용 getDynamicLink 풀이다(안드도 같은 cmd를 구현).
import { nativeDynamicLinkIOS } from '@/common/utils/module/NativeRequest';

watch(
  () => store.getters['layout/initMovePath'],
  async value => {
    if (value) {
      store.commit('layout/setInitMovePath');
      await router.push(value);
    }
  }
);

// 260519 referral attribution: native bridge가 보존 중인 deferred deep link context를
// 앱 첫 진입 시 BE로 보내 install row INSERT + referrer 매칭. trackSignup용 ctx는
// localStorage 'jump.referral.pending'에 mirror해서 가입 완료 시점까지 보존.
async function trackReferralInstall() {
  try {
    if (!window.bridge?.getPendingReferralContext) return;
    const ctx = await window.bridge.getPendingReferralContext();
    if (!ctx || !ctx.installId || !ctx.referralCode) return;
    // 260616 방식2: 6자리 숫자면 커피이벤트 추천코드 → 영속키로 저장(registerNew/SignUp Step6 자동입력).
    //   260617: 세텍 행사코드(방식3)는 4자 영숫자 → 같은 영속키로 수렴(deferred 설치 후 자동입력).
    //   배터리 추천(7자리 Base32)과 형식 구분: 6자리 숫자 또는 4자 영숫자만 커피이벤트로.
    //   260923 대표: 메가커피 이벤트 중단 — 더는 저장하지 않는다(받아 쓸 입력칸·발급이 없다). 형식 분기·consume 은 남긴다:
    //     지우면 이미 퍼진 커피 코드가 아래 일반 추천 install 추적(trackReferralInstall)으로 새어 들어간다.
    const rc = String(ctx.referralCode);
    // 260928 현판(QR) 손님 가입 경로(qr_<코드>) — 추천 추적으로 흘리지 않고 가입 경로로만(captureQrInstallReferrer 주석).
    //   이 함수까지 오는 일은 드물다(window.bridge 가 아직 없을 때가 대부분) — 브릿지가 먼저 뜬 경우의 안전판.
    if (markQrInstallReferrerOnce(rc)) {
      if (window.bridge.consumeReferralContext) { try { await window.bridge.consumeReferralContext(); } catch (_) { /* 무시 */ } }
      return;
    }
    if (/^\d{6}$/.test(rc) || /^[A-Za-z0-9]{4}$/.test(rc)) {
      if (window.bridge.consumeReferralContext) { try { await window.bridge.consumeReferralContext(); } catch (_) { /* 무시 */ } }
      return;
    }
    const payload = {
      installId: ctx.installId,
      referralCode: ctx.referralCode,
      provider: ctx.provider || 'manual',
      platform: ctx.platform || 'unknown',
      isDeferred: !!ctx.isDeferred
    };
    const { status } = await ServiceExec.post(referrals.trackInstall, payload);
    if (status !== ServiceExec.status.OK) return;
    try {
      window.localStorage.setItem('jump.referral.pending', JSON.stringify(payload));
    } catch (_) {
      /* localStorage 차단 환경 무시 — trackSignup 매칭만 포기 */
    }
    if (window.bridge.consumeReferralContext) {
      await window.bridge.consumeReferralContext();
    }
  } catch (e) {
    /* bridge 미주입 환경(웹/Android 미통합)에서 silent — 실패해도 앱 동작에 영향 X */
    console.warn('[referral] trackInstall failed:', e);
  }
}

// 260513 리텐션 보드: 앱 진입 시 ?utm_source 캐치 + 로그인 상태면 session-open 1회 기록
onMounted(() => {
  // 260812 딥링크 풀 — 네이티브가 부팅 때 push한 callJavaScript는 웹뷰가 아직 준비 전이면 그냥 사라진다
  //   (loadUrl("javascript:…") 한 줄이라 큐잉이 없다). 그래서 네이티브는 값을 들고 있다가 **FE가 물어보면**
  //   돌려주는 getDynamicLink를 이미 구현해 뒀는데(안드 MainActivity/iOS NativeCallGetDynamicLink),
  //   **FE에 그 호출부가 없어서 여태 안 쓰였다**(NativeRequest.js의 정의만 있고 호출 0건).
  //   여기서 한 번 물어본다. push와 중복돼도 무해하다 — onDynamicLink가 같은 경로를 다시 커밋할 뿐이고
  //   App.vue watch가 소비하면서 비운다. 실패는 삼킨다(앱이 아니거나 브릿지가 없으면 그냥 무시).
  //   ⚠️iOS는 커스텀 스킴 경로가 DynamicLinkManager.value를 안 채워서 빈 값이 올 수 있다(네이티브 별건).
  if (isApp()) {
    try { nativeDynamicLinkIOS(); } catch (_) { /* 브릿지 없음 — 무시 */ }
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = params.get('utm_source');
    if (utm) {
      window.localStorage.setItem('jump_utm_source', utm);
    }
  } catch (_) {
    /* localStorage 차단 환경 무시 */
  }
  // session-open은 토큰 있을 때만 발화. tracker 내부에서 isAuthed 가드.
  trackSessionOpen();
  // 260513 feature flag — 1회 fetch. 라우터 가드도 다시 dispatch하지만 캐시 hit로 noop.
  store.dispatch('common/fetchFeatureFlags');
  // 260928 현판 → 손님 가입 경로(안드 Play referrer) — window.bridge 를 기다리지 않고 네이티브 인터페이스를 직접 읽는다.
  captureQrInstallReferrer();
  // 260519 referral: native bridge에서 pending deep link 가져와 install 트래킹
  trackReferralInstall();
  // 260813 스토어 경유 설치 이월 — 웹으로 맡겨둔 QR 코드를 앱 첫 실행에서 되찾는다.
  resumePendingQrClaim();
});

/**
 * 260928 현판 → 손님 가입 경로(안드 Play referrer). 현판 '회원가입'·'저장'이 스토어로 보낼 때 referrer 에
 * `utm_content=qr_<코드>` 를 싣고(qrInstallCarry.goStoreForConsumer), 배포된 앱이 그 값을 추천 컨텍스트로 들고 있다.
 *
 * ⚠️window.bridge 를 쓰지 않는다 — 안드는 그 shim 을 onPageFinished(load 뒤)에야 주입해서 여기(onMounted)엔 늘 없다
 *   (trackReferralInstall 이 첫 줄에서 빠지는 이유, 260928 교차 검수). addJavascriptInterface 로 문서 시작부터 있는
 *   AndroidReferralBridge 를 직접 읽는다. 네이티브가 referrer 를 비동기로 받아 오므로 늦게 들어오면 두 번 더 본다.
 * ⚠️qr_ 값만 처리하고 consume 한다. 추천 코드 등 다른 값은 건드리지 않는다(기존 추천 흐름 그대로).
 */
function captureQrInstallReferrer(attempt = 0) {
  try {
    const nb = window.AndroidReferralBridge;
    if (!nb || typeof nb.getPendingReferralContextJson !== 'function') return; // iOS·웹 — 이 길이 없다(IP 맡김이 메운다)
    const raw = nb.getPendingReferralContextJson();
    if (raw) {
      const rc = JSON.parse(raw)?.referralCode;
      if (markQrInstallReferrerOnce(rc) && typeof nb.consumeReferralContext === 'function') nb.consumeReferralContext();
      return;
    }
  } catch (_) {
    return; /* 브릿지 오류 — 조용히(IP 맡김이 남는다) */
  }
  if (attempt < 2) setTimeout(() => captureQrInstallReferrer(attempt + 1), attempt === 0 ? 2000 : 6000);
}

/**
 * 260813 "앱 깔고 열었는데 뭘 해야 할지 모르겠다" 해소 — 로그인 → 정품등록 → 바인딩으로 이어준다.
 *
 * 유저: "앱미설치자가 간판온 연결하기하면 딥링크로 따라오는게 아니라 그냥 앱이 열려버려서 뭘 해야될지
 * 모르겠음. 로그인 → 정품등록 → 바인딩 이거 좀 되게 안되냐"
 *
 * 왜 네이티브(Play referrer)로 안 하나 — 그건 **앱을 새로 배포해야** 읽히고 iOS엔 수단이 없다.
 * 서버가 IP로 10분 보관한 코드를 되찾아 오면 지금 깔린 앱에서도 그대로 동작한다.
 *
 * ⚠️IP는 소유 증명이 아니다(캐리어 NAT 공유) — 그래서 **자동 등록하지 않고** 정품등록 화면까지만
 *   데려간다. 거기서 코드가 입력칸에 채워지고 등록 버튼은 사장이 누른다.
 * ⚠️로그인 전이면 라우터 가드가 로그인으로 튕기고, 로그인 후 원래 목적지로 돌아온다 — 그 계약에 얹는다.
 */
async function resumePendingQrClaim() {
  if (!isApp()) return; // 웹은 이미 그 QR 페이지에 있다 — 되찾을 이유가 없다
  try {
    const KEY = 'jump.qr.pendingClaimDone';
    if (window.sessionStorage.getItem(KEY)) return; // 한 세션에 한 번만 — 앱 안에서 계속 낚아채면 안 된다
    window.sessionStorage.setItem(KEY, '1');
    const { status, data } = await ServiceExec.get('/api/qr/pending-claim');
    const qrCode = status === ServiceExec.status.OK ? data?.qrCode : null;
    // 260828 검색에 없어 직접 적은 상호도 같이 따라온다 — 개통 화면 이름칸을 채워 준다(없으면 null).
    const bizName = status === ServiceExec.status.OK ? (data?.bizName || '') : '';
    // 260917 고른 가게 식별자 placeNo(공공데이터 tblPublicPlace) — 개통 화면이 이걸로 `/api/qr/place/one` 을 다시 조회해
    //   전체 정보를 되찾는다(값이 아니라 키를 나른다). 숫자가 아니면(null·구버전 카카오 문자열) 나르지 않는다.
    //   (260904 kakaoId 는 계획 2 에서 제거 — GET 응답에도 더는 없다.)
    const placeNo = status === ServiceExec.status.OK && isPlaceNo(data?.placeNo) ? String(data.placeNo) : '';
    if (!qrCode) return;
    // 260813 이미 연결이 끝난 코드면 조용히 끝낸다 — 스토어 버튼을 누를 때마다 코드가 맡겨지는데
    //   (goStoreWithQr), 그 사이 다른 경로로 등록을 마쳤으면 이 코드는 주인 있는 현판이다.
    //   그대로 밀면 정품등록 퍼널로 납치되고, 교회처럼 사업자번호가 없는 업체는 licensePending이
    //   영원히 참이라 **사업자 등록 페이지**가 뜬다(유저 실측 — "간판온 타일 눌렀는데 사업자 등록").
    const landing = await ServiceExec.get(`/api/qr/landing/${encodeURIComponent(qrCode)}`);
    if (landing.status === ServiceExec.status.OK && landing.data?.businessNo) return;
    // 260904 유저 "앱에서 내 매장 찾기가 계속뜬다니까" — 이름을 쿼리로만 넘기면 중간에 떨어진다.
    //   실측(nginx): 사장이 실제로 여는 주소는 /more/qr-genuine/activate?code=... 이고 name 이 없다.
    //   그 주소로 보내는 곳이 5군데인데(GenuineRegister 2 · GenuineVerify · GenuineBizRegister · QrHubPage)
    //   전부 code 만 넘긴다. 화면마다 쿼리를 이어 붙이면 또 빠지는 데가 생기므로, **앱 저장소에 한 번 적어 두고**
    //   개통 화면이 집어 가게 한다(pending-claim 은 GET 한 번에 소비돼 다시 못 읽는다).
    //   ⚠️sessionStorage 가 아니라 localStorage — 로그인 리다이렉트·웹뷰 재생성을 건너야 한다.
    try {
      if (bizName) window.localStorage.setItem(`qr.carry.name.${qrCode}`, bizName);
      if (placeNo) window.localStorage.setItem(`qr.carry.place.${qrCode}`, placeNo);
    } catch (_) { /* 무시 */ }
    await router.push(`/more/qr-genuine?code=${encodeURIComponent(qrCode)}`
      + (bizName ? `&name=${encodeURIComponent(bizName)}` : ''));
  } catch (_) {
    /* 이월 실패는 조용히 — 사장은 앱에서 QR 뒷자리를 직접 입력하면 된다 */
  }
}
</script>
